import { useEffect, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { scrollToTop } from "@/lib/utils";
import { testimonials } from "@/lib/data";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const testimonialFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  company: z.string().min(1, { message: "Company name is required" }),
  position: z.string().min(1, { message: "Position is required" }),
  rating: z.string().min(1, { message: "Please select a rating" }),
  testimonial: z.string().min(10, { message: "Testimonial must be at least 10 characters" }),
});

type TestimonialFormValues = z.infer<typeof testimonialFormSchema>;

export default function TestimonialsPage() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  
  const form = useForm<TestimonialFormValues>({
    resolver: zodResolver(testimonialFormSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      position: "",
      rating: "",
      testimonial: "",
    },
  });
  
  const testimonialMutation = useMutation({
    mutationFn: (values: TestimonialFormValues) => {
      return apiRequest("POST", "/api/testimonials", values);
    },
    onSuccess: () => {
      toast({
        title: "Testimonial Submitted",
        description: "Thank you for sharing your experience with SW Accounting!",
      });
      form.reset();
      setSubmitted(true);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  function onSubmit(values: TestimonialFormValues) {
    testimonialMutation.mutate(values);
  }
  
  useEffect(() => {
    scrollToTop();
    
    // Handle anchor links
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          setTimeout(() => {
            const headerOffset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }, 100);
        }
      }
    };
    
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);
  
  return (
    <div className="pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1a365d] mb-4">Client Testimonials</h1>
          <div className="w-24 h-1 bg-[#7d9b76] mx-auto mb-6"></div>
          <p className="max-w-3xl mx-auto text-lg">
            Hear what our clients have to say about their experience working with SW Accounting and Sarah Willardsen.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-[#f8f9fa] p-8 rounded-lg shadow-md">
              <div className="flex flex-col md:flex-row items-center mb-6">
                <div className="w-20 h-20 rounded-full bg-[#7d9b76] flex items-center justify-center text-white text-3xl mb-4 md:mb-0 md:mr-6">
                  <i className="fas fa-user"></i>
                </div>
                <div>
                  <h3 className="font-serif text-xl font-bold text-[#1a365d]">
                    {testimonial.name}
                  </h3>
                  <p className="text-[#7d9b76] font-medium">
                    {testimonial.company}
                  </p>
                </div>
              </div>
              <p className="text-lg italic mb-4">{testimonial.quote}</p>
              <div className="flex items-center justify-between">
                <div className="text-[#d9b166]">
                  {[...Array(5)].map((_, i) => (
                    <i 
                      key={i} 
                      className={`fas ${i < testimonial.rating ? 'fa-star' : i + 0.5 === testimonial.rating ? 'fa-star-half-alt' : 'fa-star'}`}
                    ></i>
                  ))}
                </div>
                <span className="text-sm text-gray-500">{testimonial.date}</span>
              </div>
            </div>
          ))}
        </div>
        
        <div id="submit" className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-md mb-16">
          <h2 className="font-serif text-3xl font-bold text-[#1a365d] mb-6 text-center">Share Your Experience</h2>
          
          {submitted ? (
            <div className="text-center p-6 bg-[#f8f9fa] rounded-lg">
              <div className="text-[#7d9b76] text-5xl mb-4">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3 className="font-serif text-2xl font-bold text-[#1a365d] mb-2">Thank You!</h3>
              <p className="mb-4">Your testimonial has been submitted successfully.</p>
              <p>We appreciate you taking the time to share your experience with SW Accounting.</p>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Full Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder="Email Address" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company</FormLabel>
                        <FormControl>
                          <Input placeholder="Your Company" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="position"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Position</FormLabel>
                        <FormControl>
                          <Input placeholder="Your Position" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="rating"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Rating</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a rating" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="5">5 Stars - Excellent</SelectItem>
                          <SelectItem value="4">4 Stars - Very Good</SelectItem>
                          <SelectItem value="3">3 Stars - Good</SelectItem>
                          <SelectItem value="2">2 Stars - Fair</SelectItem>
                          <SelectItem value="1">1 Star - Poor</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="testimonial"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Testimonial</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Please share your experience working with Sarah and SW Accounting..." 
                          className="min-h-[150px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full bg-[#7d9b76] hover:bg-opacity-90"
                  disabled={testimonialMutation.isPending}
                >
                  {testimonialMutation.isPending ? "Submitting..." : "Submit Testimonial"}
                </Button>
              </form>
            </Form>
          )}
        </div>
        
        <div className="text-center">
          <h2 className="font-serif text-3xl font-bold text-[#1a365d] mb-6">Ready to Experience the SW Accounting Difference?</h2>
          <p className="max-w-3xl mx-auto mb-8">
            Join our satisfied clients and discover how Sarah Willardsen can help transform your business's financial management.
          </p>
          <Button 
            asChild 
            className="bg-[#1a365d] hover:bg-opacity-90"
            size="lg"
          >
            <a href="/booking">Schedule a Consultation</a>
          </Button>
        </div>
      </div>
    </div>
  );
}
